import { CommonModule } from '@angular/common'
import { Component, DestroyRef, ElementRef, HostListener, inject, OnDestroy, OnInit, Renderer2, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { BetaFeature, BetaFeaturesService } from '@app/services/beta-features.service'

/**
 * Modal component for managing beta feature flags for the current user.
 * Displays non-permanent features and allows users to toggle them on/off.
 * Feature states are persisted via API and managed per-user.
 */
@Component({
  selector: 'ngx-beta-features-modal',
  templateUrl: './beta-features-modal.component.html',
  styleUrls: ['./beta-features-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class BetaFeaturesModalComponent implements OnInit, OnDestroy {
  /** Whether the modal is currently visible */
  isVisible = false

  /** List of toggleable (non-permanent) features with their enabled state */
  systemFlags = signal<BetaFeature[]>([])

  /** Track if there are unsaved changes */
  hasChanges = false
  private _destroyRef = inject(DestroyRef)

  constructor(private betaFeaturesService: BetaFeaturesService, private renderer: Renderer2, private elementRef: ElementRef) {}

  ngOnInit(): void {
    // Append modal to body for proper z-index stacking
    this.renderer.appendChild(document.body, this.elementRef.nativeElement)
  }

  ngOnDestroy(): void {
    // Clean up: remove from body when component is destroyed
    this.renderer.removeChild(document.body, this.elementRef.nativeElement)
    this.renderer.removeClass(document.body, 'beta-modal-open')
  }

  /**
   * Opens the modal and loads current feature states.
   * Uses the service to get non-permanent features with their enabled state.
   */
  open(): void {
    this.hasChanges = false
    this.isVisible = true
    this.renderer.addClass(document.body, 'beta-modal-open')

    // Get toggleable features with enabled state from service (single source of truth)
    this.systemFlags.set(this.betaFeaturesService.getToggleableFeatures())
  }

  /**
   * Closes the modal without saving
   */
  close(): void {
    this.isVisible = false
    this.renderer.removeClass(document.body, 'beta-modal-open')
  }

  /**
   * Handle escape key to close modal
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isVisible) {
      this.close()
    }
  }

  /**
   * Handle backdrop click to close modal
   */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('beta-modal-backdrop')) {
      this.close()
    }
  }

  /**
   * Toggles a feature's enabled state for the current user via API.
   * @param feature The feature to toggle
   */
  toggleFeature(feature: BetaFeature): void {
    this.hasChanges = true
    this.betaFeaturesService
      .toggleFeature(feature.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {})
  }
}
